import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Progress, Typography, Checkbox, List, Divider, Radio } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { makePostCall, getUserDetails } from '../utils/helper';
import { DICOM_PARALLEL_DOWNLOADS } from '../constants/APP_CONSTANTS';
import { FieldLabels } from '../constants/fieldLabels';

const { Title, Text } = Typography;

interface DownloadStudyModalProps {
  visible: boolean;
  onCancel: () => void;
  studyUID: string;
  studyDescription?: string;
  patientName?: string;
  accessionNumber?: string;
  studyData?: any; // Full study record from PACS table
}

const DownloadStudyModal: React.FC<DownloadStudyModalProps> = ({
  visible,
  onCancel,
  studyUID,
  studyDescription,
  patientName,
  accessionNumber,
  studyData,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [studyInstances, setStudyInstances] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState([]);
  const [downloadCancelled, setDownloadCancelled] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'DICOM' | 'JPEG' | 'PNG'>('DICOM');
  const [flatStructure, setFlatStructure] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  useEffect(() => {
    if (visible && studyUID) {
      resetStates();
      analyzeStudy();
    } else if (!visible) {
      resetAllStates();
    }
  }, [visible, studyUID]);

  const resetStates = () => {
    setStudyInstances([]);
    setSeriesList([]);
    setSelectedSeries([]);
    setDownloadCancelled(false);
    setDownloadFormat('DICOM');
    setFlatStructure(false);
    setProgress(0);
    setProgressMessage('');
    setAbortController(null);
  };

  const resetAllStates = () => {
    resetStates();
    setDownloading(false);
    setIsAnalyzing(false);
  };

  const getConfigUrl = (urlType: 'qidoRoot' | 'wadoRoot' | 'wadoUriRoot') => {
    const config = (window as any).config;
    const dicomwebSource = config?.dataSources?.find(
      (source: any) => source.sourceName === 'dicomweb'
    );
    // Fall back to the deployment's configured DICOMweb roots (ENV_CONFIG) rather
    // than a hardcoded server, so this never silently hits someone else's archive.
    const env = (window as any).ENV_CONFIG || {};
    const envFallback =
      urlType === 'wadoUriRoot' ? env.WADO_URI_ROOT :
      urlType === 'wadoRoot' ? env.WADO_ROOT :
      env.QIDO_ROOT;
    return dicomwebSource?.configuration?.[urlType] || envFallback || '';
  };

  const fetchDicomData = async (url: string) => {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  };

  const transformSeries = (series: any) => ({
    seriesUID: series['0020000E']?.Value?.[0],
    seriesDescription: series['0008103E']?.Value?.[0] || 'Unknown Series',
    modality: series['00080060']?.Value?.[0] || 'OT',
    seriesNumber: series['00200011']?.Value?.[0] || 0,
  });

  const transformInstance = (instance: any, studyUID: string, seriesUID: string) => ({
    studyUID,
    seriesUID,
    instanceUID: instance['00080018']?.Value?.[0],
    instanceNumber: instance['00200013']?.Value?.[0] || 1,
  });

  const analyzeStudy = async () => {
    setIsAnalyzing(true);
    try {
      const qidoBase = getConfigUrl('qidoRoot');
      const seriesData = await fetchDicomData(`${qidoBase}/studies/${studyUID}/series`);

      const allInstances = [];
      const seriesInfo = [];

      for (const series of seriesData) {
        const transformedSeries = transformSeries(series);
        if (transformedSeries.seriesUID) {
          try {
            const instancesData = await fetchDicomData(
              `${qidoBase}/studies/${studyUID}/series/${transformedSeries.seriesUID}/instances`
            );

            const transformedInstances = instancesData.map((instance: any) =>
              transformInstance(instance, studyUID, transformedSeries.seriesUID)
            );

            allInstances.push(...transformedInstances);
            seriesInfo.push({
              ...transformedSeries,
              instanceCount: transformedInstances.length,
            });
          } catch (error) {
            // Add series even if instances fetch fails
            seriesInfo.push({
              ...transformedSeries,
              instanceCount: 0,
            });
          }
        }
      }

      setStudyInstances(allInstances);
      setSeriesList(seriesInfo);
      setSelectedSeries(seriesInfo.map(s => s.seriesUID));

      if (allInstances.length === 0) {
        message.warning('No instances found for this study');
      }
    } catch (error) {
      message.error(`Failed to analyze study: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getWADOContentType = (format: 'DICOM' | 'JPEG' | 'PNG') => {
    switch (format) {
      case 'JPEG': return 'image/jpeg';
      case 'PNG': return 'image/png';
      default: return 'application/dicom';
    }
  };

  // Byte index of `needle` within `haystack`, searching from `start`. Byte-safe
  // (no string decoding) so it never corrupts binary DICOM payloads.
  const indexOfBytes = (haystack: Uint8Array, needle: Uint8Array, start = 0) => {
    outer: for (let i = start; i <= haystack.length - needle.length; i++) {
      for (let j = 0; j < needle.length; j++) {
        if (haystack[i + j] !== needle[j]) continue outer;
      }
      return i;
    }
    return -1;
  };

  // Pull the multipart boundary token out of a Content-Type header value.
  const getMultipartBoundary = (contentType: string | null): string | null => {
    if (!contentType) return null;
    const match = /boundary=(?:"([^"]+)"|([^;]+))/i.exec(contentType);
    return match ? (match[1] || match[2]).trim() : null;
  };

  // Extract the payload of the first part of a multipart/related body. WADO-RS
  // returns the DICOM object wrapped in a multipart envelope; we must return the
  // raw bytes only (matching what WADO-URI used to hand back), byte-for-byte.
  const extractFirstMultipartPart = (buffer: ArrayBuffer, boundary: string): Uint8Array => {
    const bytes = new Uint8Array(buffer);
    const enc = new TextEncoder();
    const boundaryBytes = enc.encode(`--${boundary}`);
    const headerSep = enc.encode('\r\n\r\n');

    const firstBoundary = indexOfBytes(bytes, boundaryBytes, 0);
    if (firstBoundary === -1) throw new Error('Multipart boundary not found in response');

    const headerEnd = indexOfBytes(bytes, headerSep, firstBoundary);
    if (headerEnd === -1) throw new Error('Multipart part headers not terminated');
    const bodyStart = headerEnd + headerSep.length;

    const nextBoundary = indexOfBytes(bytes, boundaryBytes, bodyStart);
    if (nextBoundary === -1) throw new Error('Multipart closing boundary not found');

    // The part body is followed by a CRLF before the delimiter — strip it.
    let bodyEnd = nextBoundary;
    if (bodyEnd >= 2 && bytes[bodyEnd - 2] === 0x0d && bytes[bodyEnd - 1] === 0x0a) {
      bodyEnd -= 2;
    }

    // Copy into a standalone buffer so the Blob doesn't retain the whole envelope.
    return bytes.slice(bodyStart, bodyEnd);
  };

  const fetchInstanceFromWADO = async (
    studyUID: string,
    seriesUID: string,
    instanceUID: string,
    format: 'DICOM' | 'JPEG' | 'PNG',
    signal?: AbortSignal
  ): Promise<Blob> => {
    const wadoBase = getConfigUrl('wadoRoot');
    const instanceUrl = `${wadoBase}/studies/${studyUID}/series/${seriesUID}/instances/${instanceUID}`;

    // ── DICOM: WADO-RS instance retrieve returns multipart/related ──────────────
    if (format === 'DICOM') {
      const response = await fetch(instanceUrl, {
        headers: { Accept: 'multipart/related; type="application/dicom"' },
        signal,
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch instance: ${response.status} ${response.statusText}`);
      }

      const buffer = await response.arrayBuffer();
      const boundary = getMultipartBoundary(response.headers.get('content-type'));
      // If the server ever answers single-part (no boundary), pass bytes through.
      if (!boundary) {
        return new Blob([buffer], { type: 'application/dicom' });
      }
      const dicomBytes = extractFirstMultipartPart(buffer, boundary);
      return new Blob([dicomBytes], { type: 'application/dicom' });
    }

    // ── JPEG / PNG: WADO-RS rendered returns the image directly (no multipart) ──
    const contentType = getWADOContentType(format);
    const response = await fetch(`${instanceUrl}/rendered`, {
      headers: { Accept: contentType },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch instance: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  };

  const createStudyInfoFile = () =>
    `
STUDY INFORMATION
=================
Patient Name: ${patientName || 'Unknown'}
Study Description: ${studyDescription || 'N/A'}
Study UID: ${studyUID}
Accession Number: ${accessionNumber || 'N/A'}

Generated: ${new Date().toLocaleString()}
  `.trim();

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFileExtension = (format: 'DICOM' | 'JPEG' | 'PNG') => {
    switch (format) {
      case 'JPEG': return 'jpg';
      case 'PNG': return 'png';
      default: return 'dcm';
    }
  };

  const createZipDownload = async (instances: any[], format: 'DICOM' | 'JPEG' | 'PNG', signal?: AbortSignal) => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    const studyFolderName = `${patientName || 'Unknown'}_${studyUID.slice(-8)}`;
    const studyFolder = zip.folder(studyFolderName)!;
    const ext = getFileExtension(format);
    const formatLabel = format === 'DICOM' ? 'DICOM' : format;

    // For image formats, create series subfolders unless flat structure is chosen
    const seriesFolders = new Map<string, any>();
    if (format !== 'DICOM' && !flatStructure) {
      const seriesMap = new Map(seriesList.map(s => [s.seriesUID, s]));
      for (const instance of instances) {
        if (!seriesFolders.has(instance.seriesUID)) {
          const info = seriesMap.get(instance.seriesUID);
          const folderName = info
            ? `${info.modality}_${info.seriesDescription.replace(/[^a-zA-Z0-9]/g, '_')}`
            : instance.seriesUID.slice(-8);
          seriesFolders.set(instance.seriesUID, studyFolder.folder(folderName)!);
        }
      }
    }

    let processedCount = 0;
    const totalCount = instances.length;
    const batchSize =
      typeof DICOM_PARALLEL_DOWNLOADS === 'number' && DICOM_PARALLEL_DOWNLOADS > 0
        ? DICOM_PARALLEL_DOWNLOADS
        : 10;

    // Process downloads in batches for parallel downloading
    for (let i = 0; i < instances.length; i += batchSize) {
      if (signal?.aborted) {
        throw new Error('Download cancelled by user');
      }

      const batch = instances.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(instances.length / batchSize);

      setProgressMessage(
        `Downloading batch ${batchNumber}/${totalBatches} (${batch.length} files in parallel)...`
      );

      // Download all instances in this batch in parallel
      const downloadPromises = batch.map(async instance => {
        try {
          const blob = await fetchInstanceFromWADO(
            instance.studyUID || studyUID,
            instance.seriesUID,
            instance.instanceUID,
            format,
            signal
          );
          return {
            instance,
            blob,
            success: true,
          };
        } catch (error) {
          console.error(`Failed to download instance ${instance.instanceUID}:`, error);
          return {
            instance,
            blob: null,
            success: false,
            error,
          };
        }
      });

      // Wait for all downloads in this batch to complete
      const results = await Promise.all(downloadPromises);

      // Add successfully downloaded files to ZIP
      results.forEach(result => {
        if (result.success && result.blob) {
          if (format === 'DICOM') {
            studyFolder.file(`${result.instance.instanceUID}.${ext}`, result.blob);
          } else {
            const folder = seriesFolders.get(result.instance.seriesUID) || studyFolder;
            const instanceNum = String(result.instance.instanceNumber).padStart(4, '0');
            folder.file(`${instanceNum}.${ext}`, result.blob);
          }
          processedCount++;
        }
      });

      // Update progress after each batch
      setProgress(Math.round((processedCount / totalCount) * 80));
      setProgressMessage(`Downloaded ${processedCount}/${totalCount} ${formatLabel} files...`);

      // Small delay between batches to prevent overwhelming the browser
      if (i + batchSize < instances.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    if (format !== 'DICOM') {
      studyFolder.file('study_info.txt', createStudyInfoFile());
    }

    if (signal?.aborted) {
      throw new Error('Download cancelled by user');
    }

    setProgressMessage('Creating ZIP file...');
    setProgress(80);

    const zipBlob = await zip.generateAsync(
      {
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      },
      metadata => {
        if (!signal?.aborted) {
          setProgress(Math.min(80 + Math.round(metadata.percent / 5), 100));
        }
      }
    );

    triggerDownload(zipBlob, `${studyFolderName}.zip`);
  };

  const handleDownload = async () => {
    if (studyInstances.length === 0) {
      return message.warning('No study instances available for download');
    }
    if (selectedSeries.length === 0) {
      return message.warning('Please select at least one series to download');
    }

    const instancesToDownload = studyInstances.filter(instance =>
      selectedSeries.includes(instance.seriesUID)
    );

    if (instancesToDownload.length === 0) {
      return message.warning('No instances found for selected series');
    }

    setDownloading(true);
    setDownloadCancelled(false);
    setProgress(0);
    setProgressMessage('Starting download...');
    const controller = new AbortController();
    setAbortController(controller);


    try {
      await createZipDownload(instancesToDownload, downloadFormat,  controller.signal);

      if (!downloadCancelled) {
        setProgress(100);
        setProgressMessage('Download completed!');
        message.success(`${downloadFormat} study download completed successfully!`);
        setTimeout(onCancel, 2000);
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
        message.info('Download cancelled by user');
      } else {
        message.error(`Download failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setDownloading(false);
      setDownloadCancelled(false);
      setAbortController(null);
    }
  };

  const handleCancelDownload = () => {
    setDownloadCancelled(true);
    setProgressMessage('Cancelling download...');
    abortController?.abort();
  };

  const toggleSeriesSelection = (seriesUID: string) => {
    setSelectedSeries(prev =>
      prev.includes(seriesUID) ? prev.filter(uid => uid !== seriesUID) : [...prev, seriesUID]
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DownloadOutlined />
          <span>Download Study</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button
          key="cancel"
          onClick={downloading ? handleCancelDownload : onCancel}
          disabled={isAnalyzing}
        >
          {downloading ? 'Cancel Download' : 'Close'}
        </Button>,
        <Button
          key="download"
          type="primary"
          onClick={handleDownload}
          loading={downloading}
          disabled={
            downloading || isAnalyzing || studyInstances.length === 0 || selectedSeries.length === 0
          }
          icon={<DownloadOutlined />}
        >
          Download {downloadFormat} ({selectedSeries.length} series)
        </Button>,
      ]}
      width={600}
      centered
      maskClosable={!downloading}
      closable={!downloading}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '16px',
        },
      }}
    >
      <div>
        {/* Analyzing State */}
        {isAnalyzing && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Progress
              type="circle"
              percent={50}
              status="active"
            />
            <div style={{ marginTop: '16px' }}>
              <Text>Analyzing study...</Text>
            </div>
          </div>
        )}

        {/* Study Information */}
        {!isAnalyzing && (
          <div style={{ marginBottom: '16px' }}>
            <Title
              level={5}
              style={{ marginBottom: '8px' }}
            >
              Study Information
            </Title>
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
              <div style={{ marginBottom: '4px' }}>
                <Text strong>{FieldLabels.PATIENT_NAME}: </Text>
                <Text>{patientName || 'Unknown'}</Text>
              </div>
              <div style={{ marginBottom: '4px' }}>
                <Text strong>{FieldLabels.STUDY_DESCRIPTION}: </Text>
                <Text>{studyDescription || 'N/A'}</Text>
              </div>
              <div style={{ marginBottom: '4px' }}>
                <Text strong>Study UID: </Text>
                <Text
                  code
                  style={{ fontSize: '12px' }}
                >
                  {studyUID}
                </Text>
              </div>
              {accessionNumber && (
                <div style={{ marginBottom: '4px' }}>
                  <Text strong>{FieldLabels.ACCESSION_NUMBER_SHORTEST}: </Text>
                  <Text>{accessionNumber}</Text>
                </div>
              )}
              <div>
                <Text strong>Total Instances: </Text>
                <Text>{studyInstances.length}</Text>
              </div>
            </div>
          </div>
        )}

        {/* Series Selection */}
        {!downloading && !isAnalyzing && seriesList.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <Title
                level={5}
                style={{ margin: 0 }}
              >
                Select Series to Download ({selectedSeries.length}/{seriesList.length} selected)
              </Title>
            </div>
            <List
              size="small"
              bordered
              style={{
                maxHeight: '150px',
                overflow: 'auto',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
              }}
              dataSource={seriesList}
              renderItem={series => {
                const isSelected = selectedSeries.includes(series.seriesUID);
                return (
                  <List.Item
                    key={series.seriesUID}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#f6ffed' : 'transparent',
                      border: isSelected ? '1px solid #b7eb8f' : '1px solid transparent',
                      borderRadius: '4px',
                      margin: '2px 0',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => toggleSeriesSelection(series.seriesUID)}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      style={{ marginRight: '12px', pointerEvents: 'none' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <Text strong>{series.modality}</Text>
                          <Text style={{ marginLeft: '8px' }}>{series.seriesDescription}</Text>
                        </div>
                        <Text type="secondary">
                          Series {series.seriesNumber} • {series.instanceCount} instances
                        </Text>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </div>
        )}

        {/* Format Selection */}
        {!downloading && !isAnalyzing && seriesList.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ marginBottom: '8px' }}>
              Download Format
            </Title>
            <Radio.Group
              value={downloadFormat}
              onChange={e => setDownloadFormat(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              style={{ width: '100%', display: 'flex' }}
            >
              <Radio.Button value="DICOM" style={{ flex: 1, textAlign: 'center' }}>
                DICOM (.dcm)
              </Radio.Button>
              <Radio.Button value="JPEG" style={{ flex: 1, textAlign: 'center' }}>
                JPEG (.jpg)
              </Radio.Button>
              <Radio.Button value="PNG" style={{ flex: 1, textAlign: 'center' }}>
                PNG (.png)
              </Radio.Button>
            </Radio.Group>
            {downloadFormat !== 'DICOM' && (
              <Checkbox
                checked={flatStructure}
                onChange={e => setFlatStructure(e.target.checked)}
                style={{ marginTop: '8px' }}
              >
                Download all images without folder hierarchy
              </Checkbox>
            )}
          </div>
        )}

        {/* Download Progress */}
        {downloading && (
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ display: 'block', marginBottom: '8px' }}>{progressMessage}</Text>
            <Progress
              percent={progress}
              status={progress === 100 ? 'success' : 'active'}
              showInfo={true}
            />
          </div>
        )}

        {/* Download Info */}
        {/* {!downloading && !isAnalyzing && (
          <div
            style={{
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '12px',
            }}
          >
            <Title
              level={5}
              style={{ marginBottom: '8px', color: '#0050b3' }}
            >
              Download Information
            </Title>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#0050b3' }}>
              {downloadFormat === 'DICOM' && <li>Format: Original DICOM files (.dcm)</li>}
              {downloadFormat === 'JPEG' && <li>Format: JPEG images (.jpg) - lossy compression</li>}
              {downloadFormat === 'PNG' && <li>Format: PNG images (.png) - lossless compression</li>}
              <li>Package: ZIP compressed archive</li>
              {downloadFormat !== 'DICOM' && <li>Images organized in series folders</li>}
              <li>Study information file included</li>
            </ul>
          </div>
        )} */}

        {/* Security Notice */}
        {/* <div
          style={{
            background: '#fff2e8',
            border: '1px solid #ffbb96',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '12px',
          }}
        >
          <Text
            strong
            style={{ color: '#d4380d' }}
          >
            Security Notice:
          </Text>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px', color: '#d4380d' }}>
            <li>All download activities are logged for security compliance</li>
            <li>Only authorized personnel should download patient data</li>
            <li>Handle downloaded files according to data protection policies</li>
          </ul>
        </div> */}
      </div>
    </Modal>
  );
};

export default DownloadStudyModal;
